import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Building2, Compass, Map, Layers, Box, DraftingCompass, Ruler, PencilRuler } from 'lucide-react';

const UrbanPlanning: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Metropolis Institute";
    
    return (
        <div className="min-h-screen bg-[#f0f4f8] font-sans text-slate-800 selection:bg-[#0055ff] selection:text-white border-8 border-white box-border m-2 relative">
            
            {/* Blueprint Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.1)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none z-0"></div>
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.15)_1px,transparent_1px)] bg-[size:100px_100px] pointer-events-none z-0"></div>

            {/* Corner Markers */}
            <div className="absolute top-4 left-4 w-4 h-4 border-t-2 border-l-2 border-[#0055ff] z-20"></div>
            <div className="absolute top-4 right-4 w-4 h-4 border-t-2 border-r-2 border-[#0055ff] z-20"></div>
            <div className="absolute bottom-4 left-4 w-4 h-4 border-b-2 border-l-2 border-[#0055ff] z-20"></div>
            <div className="absolute bottom-4 right-4 w-4 h-4 border-b-2 border-r-2 border-[#0055ff] z-20"></div>

            {/* Scale Marker */}
            <div className="absolute bottom-6 left-8 z-20 font-mono text-[10px] text-[#0055ff] tracking-widest hidden md:block">
                SCALE: 1:100 | SHEET: A-01 | REV: 2.0
            </div>

            {/* Header / Nav */}
            <nav className="relative z-20 px-8 py-8 flex justify-between items-start">
                <div className="bg-white border-2 border-[#0055ff] p-4 shadow-[4px_4px_0px_#0055ff]">
                    <div className="flex items-start gap-4">
                        <Building2 size={32} className="text-[#0055ff]" strokeWidth={1.5} />
                        <div>
                            <h1 className="font-bold text-xl uppercase tracking-wider leading-none mb-1">{schoolName}</h1>
                            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-[0.2em] border-t border-[#0055ff] pt-1 mt-1 w-full">
                                Department of Architecture
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hidden md:flex gap-4">
                    <Button variant="outline" className="bg-white border-2 border-slate-300 hover:border-[#0055ff] hover:text-[#0055ff] rounded-none uppercase text-xs font-bold tracking-widest h-12 px-6">
                        Projects
                    </Button>
                    <Button variant="outline" className="bg-white border-2 border-slate-300 hover:border-[#0055ff] hover:text-[#0055ff] rounded-none uppercase text-xs font-bold tracking-widest h-12 px-6">
                        Curriculum
                    </Button>
                    <Button className="bg-[#0055ff] text-white hover:bg-[#0044cc] border-2 border-[#0055ff] rounded-none uppercase text-xs font-bold tracking-widest h-12 px-6 shadow-[4px_4px_0px_rgba(0,85,255,0.3)] hover:translate-y-0.5 hover:shadow-none transition-all">
                        Enroll Now
                    </Button>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 px-8 py-20">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7 bg-white/90 backdrop-blur-sm border border-[#0055ff]/20 p-8">
                         <div className="flex items-center gap-2 text-[#0055ff] font-mono text-xs mb-6">
                            <DraftingCompass size={14} />
                            <span>CONCEPTUAL PHASE</span>
                            <div className="h-px w-20 bg-[#0055ff]"></div>
                         </div>
                         <h2 className="text-5xl md:text-7xl font-light text-slate-900 mb-8 leading-[0.9]">
                            Designing <br/>
                            <span className="font-serif italic font-medium ml-12">Tomorrow's</span> <br/>
                            Cities.
                         </h2>
                         <p className="text-lg text-slate-600 font-light leading-relaxed max-w-lg mb-12">
                            We explore the intersection of society, structure, and sustainability. Join a program that reshapes skylines and communities alike.
                         </p>
                         
                         <div className="flex gap-12 font-mono text-xs text-slate-500">
                             <div>
                                <div className="text-2xl font-bold text-[#0055ff] mb-1">04</div>
                                years B.Arch
                             </div>
                             <div>
                                <div className="text-2xl font-bold text-[#0055ff] mb-1">12</div>
                                Studios
                             </div>
                             <div>
                                <div className="text-2xl font-bold text-[#0055ff] mb-1">1:8</div>
                                Faculty Ratio
                             </div>
                         </div>
                    </div>

                    {/* Isometric Graphic Placeholder */}
                    <div className="lg:col-span-5 relative">
                        <div className="absolute inset-0 border-2 border-dashed border-[#0055ff]/30 transform translate-x-4 translate-y-4"></div>
                        <div className="relative bg-white border-2 border-[#0055ff] h-full min-h-[400px] flex items-center justify-center overflow-hidden">
                            <div className="absolute inset-0 bg-[#0055ff]/5"></div>
                            
                            {/* Abstract Building Shapes */}
                            <div className="w-32 h-64 border-2 border-[#0055ff] bg-white absolute bottom-0 left-12 transform skew-y-12 origin-bottom-left hover:bg-[#0055ff]/10 transition-colors"></div>
                            <div className="w-24 h-48 border-2 border-[#0055ff] bg-white absolute bottom-12 left-24 transform skew-y-12 origin-bottom-left z-10 hover:bg-[#0055ff]/10 transition-colors"></div>
                            <div className="w-40 h-80 border-2 border-[#0055ff] bg-white absolute bottom-[-20px] right-12 transform -skew-y-6 origin-bottom-right hover:bg-[#0055ff]/10 transition-colors"></div>
                            
                            <div className="absolute top-4 right-4 bg-[#0055ff] text-white px-2 py-1 text-[10px] font-mono font-bold">
                                FIG 1.2
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Curriculum Grid */}
            <section className="relative z-10 px-8 py-20 border-t border-[#0055ff]/20">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center gap-4 mb-12">
                        <Ruler className="text-[#0055ff]" />
                        <h3 className="font-bold text-xl uppercase tracking-widest text-[#0055ff]">Course Structure</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t-2 border-l-2 border-[#0055ff]">
                        {[
                            { icon: Map, title: "Urban Geography", num: "01" },
                            { icon: Layers, title: "Structural Integrity", num: "02" },
                            { icon: Box, title: "Spatial Logic", num: "03" }
                        ].map((item, i) => (
                            <div key={i} className="border-r-2 border-b-2 border-[#0055ff] bg-white p-8 hover:bg-[#f8faff] transition-colors group">
                                <div className="flex justify-between items-start mb-12">
                                    <span className="font-mono text-4xl font-light text-slate-300 group-hover:text-[#0055ff] transition-colors">{item.num}</span>
                                    <item.icon className="text-[#0055ff]" strokeWidth={1.5} />
                                </div>
                                <h4 className="font-bold text-lg text-slate-900 group-hover:translate-x-2 transition-transform">{item.title}</h4>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 bg-white border-t-4 border-[#0055ff] py-16 px-8 mt-12">
                 <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
                     <div>
                         <div className="flex items-center gap-2 font-bold text-xl uppercase tracking-wider text-slate-900 mb-2">
                             <PencilRuler size={24} className="text-[#0055ff]" />
                             <span>{schoolName}</span>
                         </div>
                         <p className="text-xs text-slate-500 font-mono max-w-xs">
                             Accredited by the National Architectural Accrediting Board (NAAB).
                         </p>
                     </div>
                     
                     <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="rounded-none border border-slate-200 hover:bg-[#0055ff] hover:text-white hover:border-[#0055ff]">
                                <span className="font-bold">in</span>
                          </Button>
                          <Button variant="ghost" size="icon" className="rounded-none border border-slate-200 hover:bg-[#0055ff] hover:text-white hover:border-[#0055ff]">
                                <span className="font-bold">ig</span>
                          </Button>
                          <Button variant="ghost" size="icon" className="rounded-none border border-slate-200 hover:bg-[#0055ff] hover:text-white hover:border-[#0055ff]">
                                <span className="font-bold">tw</span>
                          </Button>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default UrbanPlanning;
